import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { PaginatedStateEffects } from '@shared/ui';
import type {
  ICountCaseTypesByMethodTypeQuery,
  ICountMethodTypesQuery,
  ICountSamplesByMethodTypeQuery,
  IDeleteMethodTypeCommand,
  IListMethodTypesQuery,
} from '@protocols/use-cases';
import { combineLatest, from, merge, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { MethodTypeItem, MethodTypeListState, MethodTypeListStateModel } from '../state/method-type-list.state';
import {
  StagedMethodTypeDraft,
  StagedMethodTypeOverride,
  StagedMethodTypesState,
} from '../state/staged-method-types.state';

export const LIST_METHOD_TYPES_QUERY = new InjectionToken<IListMethodTypesQuery>('LIST_METHOD_TYPES_QUERY');
export const COUNT_METHOD_TYPES_QUERY = new InjectionToken<ICountMethodTypesQuery>('COUNT_METHOD_TYPES_QUERY');
export const COUNT_CASE_TYPES_BY_METHOD_TYPE_QUERY = new InjectionToken<ICountCaseTypesByMethodTypeQuery>(
  'COUNT_CASE_TYPES_BY_METHOD_TYPE_QUERY'
);
export const COUNT_SAMPLES_BY_METHOD_TYPE_QUERY = new InjectionToken<ICountSamplesByMethodTypeQuery>(
  'COUNT_SAMPLES_BY_METHOD_TYPE_QUERY'
);
export const DELETE_METHOD_TYPE_COMMAND = new InjectionToken<IDeleteMethodTypeCommand>('DELETE_METHOD_TYPE_COMMAND');

/*
 * Listing projection of a staged edit to a persisted method type: the pre-edit
 * catalog summary plus the edited row derived from the staged draft. Mirrors
 * `PipelineOverride`.
 */
interface MethodTypeListOverride {
  original: MethodTypeItem;
  override: MethodTypeItem;
}

@Injectable()
export class MethodTypeListEffects extends PaginatedStateEffects<MethodTypeItem, MethodTypeListStateModel> {
  constructor(
    protected readonly methodTypeState: MethodTypeListState,
    @Inject(LIST_METHOD_TYPES_QUERY)
    private readonly listMethodTypesQuery: IListMethodTypesQuery,
    @Inject(COUNT_METHOD_TYPES_QUERY)
    private readonly countMethodTypesQuery: ICountMethodTypesQuery,
    @Inject(COUNT_CASE_TYPES_BY_METHOD_TYPE_QUERY)
    private readonly countCaseTypesByMethodTypeQuery: ICountCaseTypesByMethodTypeQuery,
    @Inject(COUNT_SAMPLES_BY_METHOD_TYPE_QUERY)
    private readonly countSamplesByMethodTypeQuery: ICountSamplesByMethodTypeQuery,
    @Inject(DELETE_METHOD_TYPE_COMMAND)
    private readonly deleteMethodTypeCommand: IDeleteMethodTypeCommand,
    @Optional() private readonly stagedState: StagedMethodTypesState | null
  ) {
    super(methodTypeState);
  }

  // ============================================================================
  // Re-exposed alias selectors from MethodTypeListState
  // ============================================================================

  public readonly methodTypes$ = this.methodTypeState.methodTypes$;
  public readonly nextMethodTypeToken$ = this.methodTypeState.nextMethodTypeToken$;
  public readonly previousMethodTypeTokens$ = this.methodTypeState.previousMethodTypeTokens$;
  public readonly methodTypesLoaded$ = this.methodTypeState.methodTypesLoaded$;
  public readonly noMethodTypes$ = this.methodTypeState.noMethodTypes$;

  /*
   * Catalog-row projections of the staging ledger. The ledger stores raw drafts;
   * how a staged draft LISTS is this class's concern (`PipelineEffects` parity).
   * Empty streams when there is no staging ledger (plain catalog page).
   */
  public readonly pendingMethodTypes$: Observable<MethodTypeItem[]> = this.stagedState
    ? this.stagedState.pendingMethodTypes$.pipe(map((drafts) => drafts.map((draft) => this.toSummary(draft, true))))
    : of([]);
  public readonly methodTypeOverrides$: Observable<Record<string, MethodTypeListOverride>> = this.stagedState
    ? this.stagedState.methodTypeOverrides$.pipe(map((overrides) => this.projectOverrides(overrides)))
    : of({});

  // ============================================================================
  // Re-exposed updaters from MethodTypeListState
  // ============================================================================

  public readonly setSearchTerm = this.methodTypeState.setSearchTerm;
  public readonly changePageSize = this.methodTypeState.changePageSize;

  // ============================================================================
  // Backwards-compat effect aliases
  // ============================================================================

  public readonly deleteMethodType = this.deleteItem;
  public readonly loadMoreMethodTypes = this.loadMoreItems;
  public readonly initializeMethodTypes = this.initializeItems;
  public readonly countMethodTypes = this.countItems;

  // ============================================================================
  // Store-specific methods
  // ============================================================================

  public canBeDeleted(item: MethodTypeItem): Observable<boolean> {
    if (item.usedByCaseTypes && item.usedByCaseTypes > 0) {
      return of(false);
    } else if (item.usedByCaseTypes) {
      return of(true);
    } else {
      return from(this.countCaseTypesByMethodTypeQuery.execute(item.id)).pipe(map((count) => count === 0));
    }
  }

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected async countRepository(searchTerm?: string): Promise<number> {
    const dbCount = await this.countMethodTypesQuery.execute(searchTerm);
    if (!this.stagedState) return dbCount;

    const snapshot = await combineLatest([this.stagedState.pendingMethodTypes$, this.stagedState.methodTypeOverrides$])
      .pipe(take(1))
      .toPromise();
    if (!snapshot) return dbCount;
    const [pendingMethodTypes, methodTypeOverrides] = snapshot;
    const pendingCount = pendingMethodTypes
      .map((draft) => this.toSummary(draft, true))
      .filter((m) => this.matchesSearch(m, searchTerm)).length;
    const overrideDelta = Object.values(this.projectOverrides(methodTypeOverrides)).reduce(
      (acc, { original, override }) =>
        acc + (this.matchesSearch(override, searchTerm) ? 1 : 0) - (this.matchesSearch(original, searchTerm) ? 1 : 0),
      0
    );
    return dbCount + pendingCount + overrideDelta;
  }

  protected async deleteRepositoryItem(itemId: string): Promise<boolean> {
    try {
      await this.deleteMethodTypeCommand.execute(itemId);
      return true;
    } catch {
      return false;
    }
  }

  protected listFlow(
    limit: number,
    lastItem: MethodTypeItem | undefined,
    searchTerm: string | undefined
  ): Observable<MethodTypeItem[]> {
    const dbList$ = (dbLimit: number): Observable<MethodTypeItem[]> =>
      from(
        this.listMethodTypesQuery.execute({
          searchTerm,
          limit: dbLimit,
          lastItemName: lastItem?.name,
        })
      ).pipe(
        map((methodTypes) =>
          methodTypes.map((mt) => ({
            id: mt.id,
            name: mt.name,
            type: mt.type,
            usedByCaseTypes: undefined,
            usedBySamples: undefined,
          }))
        )
      );

    if (!this.stagedState) {
      return dbList$(limit);
    }

    return combineLatest([this.stagedState.pendingMethodTypes$, this.stagedState.methodTypeOverrides$]).pipe(
      take(1),
      switchMap(([pendingMethodTypes, methodTypeOverrides]) => {
        // Paginate by name (ascending) — matches the repository's `lastItemName`
        // cursor; do NOT borrow the createdAt cursor pipelines use.
        const cursor = lastItem?.name;
        const overrideIds = new Set(Object.keys(methodTypeOverrides));

        const pendingPage = pendingMethodTypes
          .map((draft) => this.toSummary(draft, true))
          .filter((m) => this.matchesSearch(m, searchTerm))
          .filter((m) => cursor === undefined || this.compareByName(m.name, cursor) > 0)
          .sort((a, b) => this.compareByName(a.name, b.name));

        const overridesPage = Object.values(this.projectOverrides(methodTypeOverrides))
          .map((entry) => entry.override)
          .filter((m) => this.matchesSearch(m, searchTerm))
          .filter((m) => cursor === undefined || this.compareByName(m.name, cursor) > 0)
          .sort((a, b) => this.compareByName(a.name, b.name));

        // Overfetch by |overrides| so that, after dropping stale DB rows whose id
        // is locally edited, at least `limit` DB items remain available to merge.
        const dbLimit = limit + overrideIds.size;

        return dbList$(dbLimit).pipe(
          map((dbItems) =>
            this.mergeSorted(
              pendingPage,
              overridesPage,
              dbItems.filter((m) => !overrideIds.has(m.id)),
              limit
            )
          )
        );
      })
    );
  }

  protected processDeferredAttributes(itemId: string): Observable<Partial<MethodTypeItem>> {
    const usedByCaseTypes$ = this.countAttribute(this.countCaseTypesByMethodTypeQuery.execute(itemId), itemId).pipe(
      map((usedByCaseTypes) => ({ usedByCaseTypes }))
    );
    const usedBySamples$ = this.countAttribute(this.countSamplesByMethodTypeQuery.execute(itemId), itemId).pipe(
      map((usedBySamples) => ({ usedBySamples }))
    );
    return merge(usedByCaseTypes$, usedBySamples$);
  }

  // ============================================================================
  // Pending/override merge helpers (mirror PipelineEffects, NAME-ordered)
  // ============================================================================

  /*
   * Catalog-row projection of a staged draft. `usedByCaseTypes`/`usedBySamples`
   * are left for the deferred-attribute loader; `type` is unknown from the draft.
   */
  private toSummary(draft: StagedMethodTypeDraft, pending?: boolean): MethodTypeItem {
    return {
      id: draft.id,
      name: draft.name,
      type: undefined,
      usedByCaseTypes: undefined,
      usedBySamples: undefined,
      pending: pending || undefined,
    };
  }

  private projectOverrides(
    overrides: Record<string, StagedMethodTypeOverride>
  ): Record<string, MethodTypeListOverride> {
    const projected: Record<string, MethodTypeListOverride> = {};
    for (const [id, entry] of Object.entries(overrides)) {
      projected[id] = {
        original: { id: entry.original.id, name: entry.original.name },
        override: this.toSummary(entry.draft),
      };
    }
    return projected;
  }

  private matchesSearch(methodType: MethodTypeItem, searchTerm: string | undefined): boolean {
    if (!searchTerm) return true;
    return methodType.name.toLowerCase().includes(searchTerm.toLowerCase());
  }

  private compareByName(a: string, b: string): number {
    return a.localeCompare(b);
  }

  /*
   * 3-way merge of pre-sorted (name-ascending) streams. Picks the smallest name
   * across the three indices at each step. Ties resolve pending → overrides → DB.
   */
  private mergeSorted(
    pending: MethodTypeItem[],
    overrides: MethodTypeItem[],
    db: MethodTypeItem[],
    limit: number
  ): MethodTypeItem[] {
    const streams: MethodTypeItem[][] = [pending, overrides, db];
    const idx = [0, 0, 0];
    const out: MethodTypeItem[] = [];
    while (out.length < limit) {
      let pick = -1;
      for (let s = 0; s < streams.length; s++) {
        if (idx[s] >= streams[s].length) continue;
        if (pick === -1 || this.compareByName(streams[s][idx[s]].name, streams[pick][idx[pick]].name) < 0) {
          pick = s;
        }
      }
      if (pick === -1) break;
      out.push(streams[pick][idx[pick]++]);
    }
    return out;
  }
}
